import { BarChart3, Percent } from 'lucide-react';
import FeatureModuleSection from '@/components/marketing/features/v2/FeatureModuleSection';

/** Démo illustrative — décomposition du pour cost d'une recette. */
function PourCostDemo() {
  return (
    <div className="w-full max-w-md border-[3px] border-charcoal-800 bg-charcoal-800 p-6 text-white shadow-neobrutal-lime-10">
      <div className="flex items-center justify-between border-b border-charcoal-700 pb-3">
        <span className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-200">
          Recette · Caïpirinha
        </span>
        <span className="font-marketing-mono text-[10px] uppercase tracking-[0.1em] text-lime-500">
          Pour cost 18%
        </span>
      </div>
      <ul className="mt-4 space-y-2">
        {[
          { name: 'Cachaça', qty: '50 ml', cost: '525 F' },
          { name: 'Citron vert', qty: '1/2', cost: '75 F' },
          { name: 'Sucre de canne', qty: '15 g', cost: '30 F' },
        ].map((row) => (
          <li
            key={row.name}
            className="flex items-center justify-between gap-3 border-[1.5px] border-charcoal-700 px-3 py-2 font-marketing-body text-[13px]"
          >
            <span className="text-charcoal-100">{row.name}</span>
            <span className="font-marketing-mono text-[12px] text-charcoal-300">{row.qty}</span>
            <span className="font-marketing-mono text-[12px] font-semibold text-lime-400">
              {row.cost}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-charcoal-700 pt-3 text-center">
        <div>
          <div className="font-marketing-mono text-[9px] uppercase tracking-[0.08em] text-charcoal-300">Coût</div>
          <div className="mt-1 font-marketing-display text-[15px] font-bold text-white">630 F</div>
        </div>
        <div>
          <div className="font-marketing-mono text-[9px] uppercase tracking-[0.08em] text-charcoal-300">Prix</div>
          <div className="mt-1 font-marketing-display text-[15px] font-bold text-white">3 500 F</div>
        </div>
        <div>
          <div className="font-marketing-mono text-[9px] uppercase tracking-[0.08em] text-charcoal-300">Marge</div>
          <div className="mt-1 font-marketing-display text-[15px] font-bold text-lime-500">2 870 F</div>
        </div>
      </div>
    </div>
  );
}

/** Démo illustrative — rapport d'écart valorisé en F CFA. */
function VarianceDemo() {
  return (
    <div className="w-full max-w-md border-[3px] border-charcoal-800 bg-white p-6 shadow-neobrutal-8">
      <div className="flex items-center justify-between border-b-2 border-charcoal-200 pb-3">
        <span className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-500">
          Rapport d’écart · semaine
        </span>
        <span className="font-marketing-mono text-[10px] uppercase tracking-[0.1em] text-terracotta-400">
          −3 410 F
        </span>
      </div>
      <ul className="mt-4 space-y-2">
        {[
          { name: 'Rhum blanc', delta: '−150 ml', value: '−1 714 F', bad: true },
          { name: 'Gin', delta: '−90 ml', value: '−1 296 F', bad: true },
          { name: 'Citron vert', delta: '−18 u', value: '−400 F', bad: true },
          { name: 'Sucre de canne', delta: '+0 g', value: '0 F', bad: false },
        ].map((row) => (
          <li
            key={row.name}
            className="flex items-center justify-between gap-3 border-2 border-charcoal-200 px-3 py-2 font-marketing-body text-[13px]"
          >
            <span className="font-medium text-charcoal-700">{row.name}</span>
            <span className="font-marketing-mono text-[12px] text-charcoal-500">{row.delta}</span>
            <span
              className={`font-marketing-mono text-[12px] font-semibold ${row.bad ? 'text-terracotta-500' : 'text-charcoal-400'}`}
            >
              {row.value}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center justify-between border-2 border-lime-500 bg-lime-50 px-3 py-2.5 font-marketing-mono text-[11px] uppercase tracking-[0.06em] text-charcoal-700">
        <span>Coupable n°1</span>
        <span className="font-semibold text-charcoal-800">Rhum · surdosage</span>
      </div>
    </div>
  );
}

export default function BarFeatureModulesV2() {
  return (
    <>
      <FeatureModuleSection
        id="pour-cost"
        num="01 / Pour cost"
        eyebrow="Recettes & pour cost"
        sectionLabel="Ce que tu obtiens"
        title={
          <>
            Le pour cost de chaque boisson, <span className="underline-lime">calculé tout seul.</span>
          </>
        }
        description="Compose ta recette en décimal — 50 ml de rhum, 30 ml de citron, 15 g de sucre — et Velko Bar calcule le coût exact, la marge et le pour cost % en temps réel. Plus besoin de deviner si tu vends ton cocktail au bon prix."
        bullets={[
          'Coût exact par millilitre ou par gramme (conversion d’unités gérée)',
          'Pour cost % et marge affichés en temps réel sur chaque produit',
          'Une seule recette active par produit, versionnée proprement',
          'Idéal pour fixer tes prix et viser une marge cible sur les boissons',
        ]}
        demo={<PourCostDemo />}
        Icon={Percent}
        tone="paper"
      />

      <FeatureModuleSection
        id="rapport-ecart"
        num="02 / Écart"
        eyebrow="Rapport d’écart"
        sectionLabel="Ce que tu obtiens"
        title={
          <>
            Le rapport d’écart qui <span className="underline-lime">chiffre ce qui fuit.</span>
          </>
        }
        description="Velko compare le stock théorique (déduit des ventes) au stock réel (compté à l’aveugle) et valorise l’écart en francs CFA, ingrédient par ingrédient. Tu sais quel produit fuit, de combien, et ce que ça te coûte sur la période."
        bullets={[
          'Écart en millilitres, en pourcentage et valorisé en F CFA',
          'Détection automatique du surdosage au-delà de ton seuil',
          'Classement des ingrédients qui fuient le plus (top coulage)',
          'Période paramétrable : service, semaine ou mois',
        ]}
        demo={<VarianceDemo />}
        Icon={BarChart3}
        reversed
        tone="white"
      />
    </>
  );
}
